import { Eraser } from 'lucide-react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'

/**
 * Touch-first signature pad.
 *
 * Why we wrote it ourselves:
 *   - Existing libs (`react-signature-canvas` etc.) drag in old
 *     React 16 typings + bundle 30+ KB for what is, at heart, a
 *     PointerEvent → CanvasRenderingContext2D loop.
 *   - We need a `Blob` (PNG) directly, not a base64 data URL — the
 *     mission upload route expects multipart files. Custom code
 *     keeps the blob path obvious.
 *
 * Implementation choices:
 *   - **PointerEvents** unify mouse + touch + pen in one listener
 *     set, including pressure. That's the only modern way to draw
 *     reliably on tablets, phones and laptop test rigs at the same
 *     time.
 *   - **Device-pixel-ratio scaling**: we store the canvas at
 *     `cssWidth × dpr` so the line stays crisp on retina screens.
 *     Re-runs on resize / DPR change because rotating a phone from
 *     portrait to landscape shifts both.
 *   - **`requestAnimationFrame`** is *not* used per stroke point —
 *     drawing happens directly inside the pointermove handler.
 *     Pointer events already fire at 60 Hz on iOS / Android, and a
 *     deferred draw introduces a perceptible lag on signatures
 *     made by people in a hurry.
 *   - **Blob extraction** is debounced via `pointerup` only — we
 *     don't re-encode mid-stroke.
 *
 * Parent contract: `onSignatureChange(blob | null)` is called on
 * every meaningful state change (stroke ended, pad cleared). The
 * parent decides what to do with the blob; this component does
 * not own the upload.
 */

interface SignaturePadProps {
  label: string
  /** Called every time the signature changes. `null` when cleared. */
  onSignatureChange: (blob: Blob | null) => void
  /**
   * Optional fixed height in pixels. Defaults to 200px — fits
   * comfortably above the on-screen keyboard on 5-inch phones.
   */
  height?: number
}

export function SignaturePad({
  label,
  onSignatureChange,
  height = 200,
}: SignaturePadProps) {
  const labelId = useId()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingRef = useRef<boolean>(false)
  const lastPointRef = useRef<{ x: number; y: number } | null>(null)
  const [hasInk, setHasInk] = useState<boolean>(false)

  /* ------------------------------------------------------------------ */
  /*  Canvas sizing — keep CSS pixels and backing pixels in sync.        */
  /* ------------------------------------------------------------------ */

  const resize = useCallback(() => {
    const canvas = canvasRef.current
    if (canvas === null) return
    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    canvas.width = Math.round(rect.width * dpr)
    canvas.height = Math.round(rect.height * dpr)
    const ctx = canvas.getContext('2d')
    if (ctx === null) return
    // Scale the drawing context once so all subsequent moveTo /
    // lineTo calls work in CSS pixel space.
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.scale(dpr, dpr)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = 2
    ctx.strokeStyle = '#111827' // close-to-black, matches token text
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, rect.width, rect.height)
  }, [])

  useEffect(() => {
    resize()
    window.addEventListener('resize', resize)
    window.addEventListener('orientationchange', resize)
    return () => {
      window.removeEventListener('resize', resize)
      window.removeEventListener('orientationchange', resize)
    }
  }, [resize])

  /* ------------------------------------------------------------------ */
  /*  Drawing                                                            */
  /* ------------------------------------------------------------------ */

  function pointFor(event: React.PointerEvent<HTMLCanvasElement>): {
    x: number
    y: number
  } {
    const rect = event.currentTarget.getBoundingClientRect()
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }
  }

  function onPointerDown(event: React.PointerEvent<HTMLCanvasElement>): void {
    event.currentTarget.setPointerCapture(event.pointerId)
    drawingRef.current = true
    const point = pointFor(event)
    lastPointRef.current = point
    const ctx = canvasRef.current?.getContext('2d')
    if (ctx !== null && ctx !== undefined) {
      ctx.beginPath()
      ctx.moveTo(point.x, point.y)
      // A single tap should produce a dot, not nothing.
      ctx.lineTo(point.x + 0.01, point.y + 0.01)
      ctx.stroke()
    }
    if (!hasInk) setHasInk(true)
  }

  function onPointerMove(event: React.PointerEvent<HTMLCanvasElement>): void {
    if (!drawingRef.current) return
    const ctx = canvasRef.current?.getContext('2d')
    const last = lastPointRef.current
    if (ctx === null || ctx === undefined || last === null) return
    const point = pointFor(event)
    ctx.beginPath()
    ctx.moveTo(last.x, last.y)
    ctx.lineTo(point.x, point.y)
    ctx.stroke()
    lastPointRef.current = point
  }

  function endStroke(event: React.PointerEvent<HTMLCanvasElement>): void {
    if (!drawingRef.current) return
    drawingRef.current = false
    lastPointRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    emitBlob()
  }

  /* ------------------------------------------------------------------ */
  /*  Blob extraction & clear                                            */
  /* ------------------------------------------------------------------ */

  function emitBlob(): void {
    const canvas = canvasRef.current
    if (canvas === null) {
      onSignatureChange(null)
      return
    }
    canvas.toBlob(
      (blob) => {
        onSignatureChange(blob)
      },
      'image/png',
      // PNG ignores quality; pass 1 anyway in case we swap to JPEG
      // later for smaller signatures.
      1,
    )
  }

  function clear(): void {
    const canvas = canvasRef.current
    if (canvas !== null) {
      const ctx = canvas.getContext('2d')
      const rect = canvas.getBoundingClientRect()
      if (ctx !== null) {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, rect.width, rect.height)
      }
    }
    setHasInk(false)
    onSignatureChange(null)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span id={labelId} className="text-sm font-medium text-text">
          {label}
        </span>
        <button
          type="button"
          onClick={clear}
          disabled={!hasInk}
          className={[
            'inline-flex items-center gap-1 text-sm',
            'min-h-[2.5rem] px-3 rounded-lg',
            'transition-colors duration-100',
            hasInk
              ? 'text-text-muted hover:text-text hover:bg-surface-muted active:bg-surface-muted'
              : 'text-text-muted opacity-40 cursor-not-allowed',
          ].join(' ')}
          aria-label="Effacer la signature"
        >
          <Eraser size={16} aria-hidden="true" />
          Effacer
        </button>
      </div>

      <canvas
        ref={canvasRef}
        aria-labelledby={labelId}
        // `touch-none` disables the browser's built-in scroll
        // gesture so dragging on the canvas draws instead of
        // panning the page.
        className={[
          'w-full rounded-xl border-2 border-surface-border bg-surface',
          'touch-none cursor-crosshair',
        ].join(' ')}
        style={{ height: `${height}px` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endStroke}
        onPointerLeave={endStroke}
        onPointerCancel={endStroke}
      />

      {!hasInk ? (
        <p className="text-xs text-text-muted">Signez avec votre doigt ou un stylet.</p>
      ) : null}
    </div>
  )
}
